import { useEffect, useRef } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { focusManager } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { isWeb } from '@/lib/platform';

const PERIODIC_REFRESH_INTERVAL_MS = 60 * 1000; // refresh active queries every 60s

const initialAppState = isWeb ? 'active' : AppState.currentState;

interface UseAppDataRefreshOptions {
  refreshIntervalMs?: number;
}

export function useAppDataRefresh({
  refreshIntervalMs = PERIODIC_REFRESH_INTERVAL_MS,
}: UseAppDataRefreshOptions = {}) {
  const isAppActiveRef = useRef(initialAppState === 'active');

  useEffect(() => {
    if (isWeb) {
      const doc = (globalThis as any).document;
      if (!doc) {
        return;
      }

      const handleVisibilityChange = () => {
        isAppActiveRef.current = !doc.hidden;
      };

      handleVisibilityChange();
      doc.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        doc.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }

    const handleAppStateChange = (status: AppStateStatus) => {
      const isActive = status === 'active';
      isAppActiveRef.current = isActive;
      focusManager.setFocused(isActive);

      if (isActive) {
        void queryClient.invalidateQueries({ refetchType: 'active' });
      }
    };

    handleAppStateChange(AppState.currentState);
    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => {
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    if (!refreshIntervalMs || refreshIntervalMs <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      if (!isAppActiveRef.current) {
        return;
      }

      void queryClient.refetchQueries({ type: 'active' });
    }, refreshIntervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [refreshIntervalMs]);
}
